import {
  type EarlyRepaymentSummary,
  EarlyRepaymentType,
  type IEarlyRepayment,
  type IEarlyRepaymentDescriptor,
} from "./types";

const summarise = (terms: IEarlyRepayment): EarlyRepaymentSummary => {
  const { type, unit, value } = terms;

  // A `value` of 0 is a real answer, so test for null/undefined rather than
  // falsiness — and `!= null` rather than `!== null` so an `undefined` unit
  // or value (e.g. a hand-built payload) is treated the same as `null`
  // instead of being read as "present" and formatting as NaN.
  const hasFee = unit != null && value != null;

  switch (type) {
    case EarlyRepaymentType.FREE:
      return { kind: "free" };

    case EarlyRepaymentType.FULL_INTEREST_AND_FEES:
      return { kind: "fullInterestAndFees" };

    case EarlyRepaymentType.FREE_AFTER_PERIOD:
      return hasFee && terms.paidPeriodMonths != null
        ? {
            kind: "freeAfterPeriod",
            months: terms.paidPeriodMonths,
            unit,
            value,
          }
        : { kind: "typeOnly", type: EarlyRepaymentType.FREE_AFTER_PERIOD };

    case EarlyRepaymentType.FEE_APPLIES:
      return hasFee
        ? { kind: "feeApplies", unit, value }
        : { kind: "typeOnly", type: EarlyRepaymentType.FEE_APPLIES };

    default:
      return { kind: "unspecified" };
  }
};

/**
 * Turns the stored terms into something a UI can render without guessing.
 *
 * Replaces four separate `/^\d+(\.\d+)?$/` sniffs that tried to infer a unit
 * from free text and silently mis-rendered anything mixed, e.g. the real
 * stored value "Some explaining term: 1000.00".
 *
 * Neither `typeOnly` nor `unspecified` is an error state: the migration
 * leaves `unit`/`value` null where legacy text could not be typed. `typeOnly`
 * still knows WHICH terms apply and must render their name. Never substitute
 * 0 for a missing value.
 */
export const describeEarlyRepayment = (
  terms: IEarlyRepayment,
): IEarlyRepaymentDescriptor => {
  const trimmed = terms.comment?.trim();

  return {
    summary: summarise(terms),
    comment: trimmed ? trimmed : null,
  };
};
